// apps/web/src/pages/estoque/revisao-grade.tsx — Fase 13, Plano 29 (EST-13, D-24, D-25, D-26).
// Grade da REVISÃO do inventário (situação "Aguardando aprovação"), só para quem tem `estoque:aprovar-ajuste`:
// Item · Contado · Diferença · Justificativa na linha; Saldo esperado, Movimentado durante a contagem e Impacto no valor
// vão para a linha expandida em tablet (sem rolagem horizontal) e viram colunas a partir de `xl`. Linhas com diferença
// vêm primeiro; "Mostrar só itens com diferença" vem marcado. A diferença é contado − esperado no corte (D-25): o que
// entrou ou saiu depois só aparece como informação.
import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, ClipboardCheck } from 'lucide-react';
import type { DonoTipo, ItemRevisao } from '@pieltec/contracts';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import { formatarQuantidadeEstoque } from '../../lib/estoque/formatacao';
import {
  APOIO_CORTE,
  MENSAGEM_JUSTIFICATIVA_OBRIGATORIA,
  MENSAGEM_SEM_DIFERENCA,
  PLACEHOLDER_JUSTIFICATIVA,
  apoioDaLinha,
  exigeJustificativa,
  filtrarComDiferenca,
  justificativaFixa,
  mostraImpacto,
  ordenarRevisao,
  textoDiferenca,
  textoImpacto,
  textoMovimentado,
  type JustificativasRevisao,
} from '../../lib/estoque/inventario-revisao';

const CABECALHO = 'px-3 py-2 text-xs font-semibold uppercase tracking-wider';

export interface RevisaoGradeProps {
  readonly itens: readonly ItemRevisao[];
  readonly donoTipo: DonoTipo;
  readonly justificativas: JustificativasRevisao;
  readonly aoAlterarJustificativa: (itemId: string, texto: string) => void;
  /** itens cujas justificativas faltam depois de uma tentativa de aprovar: destaca o campo. */
  readonly itensComErro: ReadonlySet<string>;
  readonly desabilitada: boolean;
}

function Diferenca({ item }: { item: ItemRevisao }) {
  if (item.diferenca === 0) {
    return <span className="text-xs font-medium text-slate-500">{MENSAGEM_SEM_DIFERENCA}</span>;
  }
  return (
    <span className="inline-flex items-center gap-1 text-sm font-semibold tabular-nums text-amber-700">
      <ClipboardCheck className="size-4 shrink-0" aria-hidden="true" />
      {textoDiferenca(item.diferenca, item.unidade)}
    </span>
  );
}

function DetalhesSecundarios({ item, comImpacto }: { item: ItemRevisao; comImpacto: boolean }) {
  return (
    <dl className="grid gap-x-6 gap-y-1 text-xs sm:grid-cols-3">
      <div>
        <dt className="text-muted-foreground">Saldo esperado</dt>
        <dd className="font-medium tabular-nums">{formatarQuantidadeEstoque(item.esperadoNoCorte, item.unidade)}</dd>
      </div>
      <div>
        <dt className="text-muted-foreground">Movimentado durante a contagem</dt>
        <dd className="font-medium tabular-nums">{textoMovimentado(item.movimentadoDepoisDoCorte, item.unidade)}</dd>
      </div>
      {comImpacto && (
        <div>
          <dt className="text-muted-foreground">Impacto no valor</dt>
          <dd className="font-medium tabular-nums">{textoImpacto(item.impactoValor)}</dd>
        </div>
      )}
    </dl>
  );
}

interface LinhaRevisaoProps {
  readonly item: ItemRevisao;
  readonly comImpacto: boolean;
  readonly justificativa: string;
  readonly comErro: boolean;
  readonly desabilitada: boolean;
  readonly aoAlterar: (texto: string) => void;
}

function LinhaRevisao({ item, comImpacto, justificativa, comErro, desabilitada, aoAlterar }: LinhaRevisaoProps) {
  const [expandida, setExpandida] = useState(false);
  const fixa = justificativaFixa(item);
  const exige = exigeJustificativa(item);
  const apoios = apoioDaLinha(item);
  const destaque = item.diferenca !== 0 ? 'bg-amber-50/50' : '';
  const Seta = expandida ? ChevronDown : ChevronRight;

  return (
    <>
      <tr className={`border-t align-top ${destaque}`}>
        <td className="w-full max-w-0 px-3 py-2">
          <div className="flex items-start gap-1">
            <button
              type="button"
              className="mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-md hover:bg-slate-100 xl:hidden"
              aria-expanded={expandida}
              aria-label={`${expandida ? 'Recolher' : 'Expandir'} detalhes de ${item.codigo}`}
              onClick={() => setExpandida((atual) => !atual)}
            >
              <Seta className="size-4" aria-hidden="true" />
            </button>
            <div className="min-w-0">
              <div className="font-semibold tabular-nums">{item.codigo}</div>
              <div className="truncate text-muted-foreground" title={item.descricao}>{item.descricao}</div>
              {apoios.map((apoio) => (
                <p key={apoio} className="mt-1 text-xs text-amber-700">
                  {apoio}
                </p>
              ))}
            </div>
          </div>
        </td>
        <td className="px-3 py-3 text-right tabular-nums">{formatarQuantidadeEstoque(item.contado, item.unidade)}</td>
        <td className="hidden px-3 py-3 text-right tabular-nums xl:table-cell">{formatarQuantidadeEstoque(item.esperadoNoCorte, item.unidade)}</td>
        <td className="hidden px-3 py-3 text-right tabular-nums xl:table-cell">{textoMovimentado(item.movimentadoDepoisDoCorte, item.unidade)}</td>
        <td className="px-3 py-3 text-right">
          <Diferenca item={item} />
        </td>
        {comImpacto && <td className="hidden px-3 py-3 text-right tabular-nums xl:table-cell">{item.diferenca === 0 ? '—' : textoImpacto(item.impactoValor)}</td>}
        <td className="min-w-48 px-3 py-2 md:min-w-64">
          {fixa ? (
            <p className="py-1.5 text-sm text-muted-foreground">{fixa}</p>
          ) : exige ? (
            <>
              <Textarea
                id={`revisao-j-${item.itemId}`}
                rows={1}
                disabled={desabilitada}
                value={justificativa}
                placeholder={PLACEHOLDER_JUSTIFICATIVA}
                aria-label={`Justificativa de ${item.codigo}`}
                aria-invalid={comErro}
                onChange={(e) => aoAlterar(e.target.value)}
                className="min-h-11 sm:min-h-9"
              />
              {comErro && <p className="mt-1 text-xs text-rose-700">{MENSAGEM_JUSTIFICATIVA_OBRIGATORIA}</p>}
            </>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </td>
      </tr>
      {expandida && (
        <tr className={`xl:hidden ${destaque}`}>
          <td colSpan={5} className="bg-slate-50 px-4 py-2">
            <DetalhesSecundarios item={item} comImpacto={comImpacto} />
          </td>
        </tr>
      )}
    </>
  );
}

export function RevisaoGrade({ itens, donoTipo, justificativas, aoAlterarJustificativa, itensComErro, desabilitada }: RevisaoGradeProps) {
  const [soComDiferenca, setSoComDiferenca] = useState(true);
  const comImpacto = mostraImpacto(donoTipo);
  const ordenados = useMemo(() => ordenarRevisao(itens), [itens]);
  const exibidos = useMemo(() => filtrarComDiferenca(ordenados, soComDiferenca), [ordenados, soComDiferenca]);

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-2 px-3 py-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">Saldo esperado considera as entradas e saídas feitas desde o início da contagem.</p>
          <label htmlFor="revisao-so-diferenca" className="flex min-h-11 cursor-pointer items-center gap-2 text-sm sm:min-h-9">
            <Checkbox
              id="revisao-so-diferenca"
              checked={soComDiferenca}
              onCheckedChange={(marcada) => setSoComDiferenca(marcada === true)}
              className="size-5 sm:size-4"
            />
            Mostrar só itens com diferença
          </label>
        </div>
        <p className="text-xs text-muted-foreground">{APOIO_CORTE}</p>
      </div>

      {exibidos.length === 0 ? (
        <p className="px-3 py-8 text-center text-sm text-muted-foreground">Nenhum item com diferença. Desmarque o filtro para ver todos os itens contados.</p>
      ) : (
        <table className="w-full text-sm">
          <caption className="sr-only">Revisão da contagem do inventário</caption>
          <thead className="bg-slate-100 text-left">
            <tr>
              <th scope="col" className={CABECALHO}>Item</th>
              <th scope="col" className={`${CABECALHO} text-right`}>Contado</th>
              <th scope="col" className={`${CABECALHO} hidden text-right xl:table-cell`}>Saldo esperado</th>
              <th scope="col" className={`${CABECALHO} hidden text-right xl:table-cell`}>Movimentado durante a contagem</th>
              <th scope="col" className={`${CABECALHO} text-right`}>Diferença</th>
              {comImpacto && <th scope="col" className={`${CABECALHO} hidden text-right xl:table-cell`}>Impacto no valor</th>}
              <th scope="col" className={CABECALHO}>Justificativa</th>
            </tr>
          </thead>
          <tbody>
            {exibidos.map((item) => (
              <LinhaRevisao
                key={item.itemId}
                item={item}
                comImpacto={comImpacto}
                justificativa={justificativas[item.itemId] ?? ''}
                comErro={itensComErro.has(item.itemId)}
                desabilitada={desabilitada}
                aoAlterar={(texto) => aoAlterarJustificativa(item.itemId, texto)}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
